import React, { useMemo } from 'react';
import { OrganizationComponent, UiComponent } from 'components';
import { OrganizationQuery } from 'queries';
import { useIsMobile } from 'hooks';
import useModal from 'hooks/useModal';
import { IOrganization } from 'types';
import { toast } from 'react-toastify';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { tableWrap } from 'styles/table';

const COLS = ['로고', '내부 식별자', '표시명', '설명', '생성일', '액션'];

function List() {
  const isMobile = useIsMobile();
  const { handleModal, handleConfirm } = useModal();
  const { data, isLoading } = OrganizationQuery.useGetAll();
  const { mutateAsync: update } = OrganizationQuery.useUpdate();

  const openForm = (organization: IOrganization | null) => {
    handleModal(
      organization ? '단체 수정' : '단체 등록',
      <OrganizationComponent.Form organization={organization} />,
      <></>,
    );
  };

  const toggleDisabled = (org: IOrganization) => {
    const next = !org.disabled;
    handleConfirm({
      confirmContent: `'${org.displayName}' 단체를 ${next ? '비활성화' : '활성화'}하시겠습니까?`,
      confirmNext: () => {
        update({ id: org.id, disabled: next })
          .then(() =>
            toast.success(next ? '비활성화되었습니다' : '활성화되었습니다'),
          )
          .catch(() => toast.error(DEFAULT_ERROR_MESSAGE));
      },
    });
  };

  const renderer = useMemo(
    () => ({
      로고: ({ logoUrl, displayName }: IOrganization) =>
        logoUrl ? (
          <img
            src={logoUrl}
            alt={displayName}
            css={{
              width: 40,
              height: 40,
              objectFit: 'contain',
              borderRadius: 4,
              background: '#f5f5f5',
            }}
          />
        ) : (
          <div
            css={{
              width: 40,
              height: 40,
              background: '#f0f0f0',
              borderRadius: 4,
            }}
          />
        ),
      '내부 식별자': ({ name }: IOrganization) => <>{name}</>,
      표시명: ({ displayName }: IOrganization) => <>{displayName}</>,
      설명: ({ description }: IOrganization) => <>{description || '-'}</>,
      생성일: ({ createdAt }: IOrganization) => <>{createdAt?.slice(0, 10)}</>,
      액션: (org: IOrganization) => (
        <div css={{ display: 'flex', gap: 4 }}>
          <UiComponent.Button
            variant="border"
            color="primary"
            onClick={() => openForm(org)}
          >
            수정
          </UiComponent.Button>
          {/* <UiComponent.Button
            variant="border"
            color="primary"
            onClick={() => toggleDisabled(org)}
          >
            {org.disabled ? '활성화' : '비활성화'}
          </UiComponent.Button> */}
        </div>
      ),
    }),
    [],
  );

  const rows = (data ?? []).map((o) => ({ ...o, rowKey: o.id }));

  if (isMobile) {
    return (
      <>
        <UiComponent.PageTitle>단체 관리</UiComponent.PageTitle>
        <OrganizationComponent.MobileList
          data={data ?? []}
          isLoading={isLoading}
        />
      </>
    );
  }

  return (
    <>
      <UiComponent.PageTitle>단체 관리</UiComponent.PageTitle>
      <UiComponent.Box>
        <div
          css={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: 12,
          }}
        >
          <UiComponent.Button
            variant="contained"
            color="primary"
            onClick={() => openForm(null)}
          >
            단체 등록
          </UiComponent.Button>
        </div>
        <div css={tableWrap}>
          <UiComponent.Table
            cols={COLS}
            renderer={renderer}
            data={rows}
            sticky
            empty="등록된 단체가 없습니다"
          />
        </div>
      </UiComponent.Box>
    </>
  );
}

export default List;
